/**
 * A deleted account's storage taken off the device (part 36c, table FD, row FD9; the user's rule BK17, 2026-10-24: an
 * account deleted takes its books on the device with it): its book store and backup log (IndexedDB) and its records of
 * typing not stored yet (`localStorage`, by their prefix). Run at a page's start, before any book store opens; a
 * database another page still holds open blocks its deletion, which is then tried again at the next start.
 */
import type { UnsavedStorage } from '../shell/unsaved.ts';
import type { DoomedNames } from './store.ts';

/** A delete's request, as far as used: its three events' handlers (their event is never read). */
interface DeleteRequest {
  onsuccess: ((...event: never[]) => unknown) | null;
  onerror: ((...event: never[]) => unknown) | null;
  onblocked: ((...event: never[]) => unknown) | null;
}

/** The browser's databases (`indexedDB`), as far as a delete uses them. */
export interface Databases {
  deleteDatabase(name: string): DeleteRequest;
}

/** Deletes the database `name`: true once it is gone (or never was), false when a page holds it open or the browser refuses. */
export function deleteDatabase(factory: Databases, name: string): Promise<boolean> {
  return new Promise((resolve) => {
    const request = factory.deleteDatabase(name);
    request.onsuccess = () => {
      resolve(true);
    };
    request.onerror = () => {
      resolve(false);
    };
    request.onblocked = () => {
      resolve(false);
    };
  });
}

/** Removes every record whose key begins with `prefix`; storage that can't be reached holds none this page can see. */
function removeUnsaved(storage: () => UnsavedStorage, prefix: string): void {
  let records: UnsavedStorage;
  try {
    records = storage();
  } catch {
    return;
  }
  for (let i = records.length - 1; i >= 0; i -= 1) {
    const key = records.key(i);
    if (key?.startsWith(prefix) === true) records.removeItem(key);
  }
}

/** FD9: what deletes a deleted account's storage on `factory` and `storage` (the page's start hands it the account). */
export function spaceEraser(factory: Databases, storage: () => UnsavedStorage): (names: DoomedNames) => Promise<boolean> {
  return (names) => eraseSpace(factory, storage, names);
}

/** FD9: deletes `names`' storage: true once the book store and the backup log are gone and the unsaved records removed. */
export async function eraseSpace(factory: Databases, storage: () => UnsavedStorage, names: DoomedNames): Promise<boolean> {
  if (!(await deleteDatabase(factory, names.books)) || !(await deleteDatabase(factory, names.backups))) return false;
  removeUnsaved(storage, names.unsaved);
  return true;
}
